import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import axios from 'axios'

import api, {
    MutationRequestOptions,
    getMutationRequestConfig,
} from '../utils/axiosConfig'
import {
    HistoryAnnotationModelConfig,
    HistoryAnnotationSource,
    HistoryExportJob,
    HistoryExportJobDownload,
    HistoryListItem,
    HistoryPrediction,
    HistoryRecord,
    HistoryReviewStatus,
    HistoryTotalStatus,
    PaginatedHistoryListResponse,
    ManualRecognitionObjectInput,
} from '../types/history'
import { normalizeSearchValues } from '../utils/searchText'
import { hydrateHistoryRecordsWithSignedUrls } from './ossService'
const paginatedRequests = new Map<
    string,
    Promise<PaginatedHistoryListResponse>
>()
const detailRequests = new Map<string, Promise<HistoryRecord | null>>()
const manualDetailRequests = new Map<string, Promise<HistoryRecord | null>>()

export interface PrefetchHistoryRecordsOptions {
    limit?: number
    concurrency?: number
}

interface ExportHistoryDatasetPayload {
    history_ids?: string[]
    review_statuses?: HistoryReviewStatus[]
    annotation_source?: HistoryAnnotationSource
    start_timestamp?: number
    end_timestamp?: number
}

interface ExportHistoryDatasetResult {
    success: boolean
    errorMessage?: string | null
}

interface CreateHistoryExportJobResult {
    success: boolean
    job?: HistoryExportJob
    errorMessage?: string | null
}

interface GetHistoryExportJobResult {
    success: boolean
    job?: HistoryExportJob
    errorMessage?: string | null
}

interface ListHistoryExportJobsResult {
    success: boolean
    jobs: HistoryExportJob[]
    errorMessage?: string | null
}

interface GetHistoryExportJobDownloadResult {
    success: boolean
    download?: HistoryExportJobDownload
    errorMessage?: string | null
}

export interface HistoryListQueryParams {
    page?: number
    page_size?: number
    type?: 'image'
    sort_by?: 'timestamp' | 'review_updated_at'
    sort_order?: 'asc' | 'desc'
    start_timestamp?: number
    end_timestamp?: number
    region_id?: number
    region_ids?: number[]
    filename?: string
    device_keyword?: string
    device_name?: string
    review_status?: HistoryReviewStatus
    mode?: 'history' | 'annotation' | 'review_export'
    include_review_preview?: boolean
    manual_recognition_status?: 'marked' | 'unmarked'
    pest_types?: string[]
}

interface UseHistoryOptions extends HistoryListQueryParams {
    forceRefreshKey?: number
}

interface RefreshHistoryRecordsOptions {
    silent?: boolean
}

const HISTORY_EXPORT_TASK_API_UNAVAILABLE_MESSAGE =
    'History export task API is not available on the current server'

const getFilenameFromDisposition = (headerValue?: string): string | null => {
    if (!headerValue) {
        return null
    }

    const utf8Match = headerValue.match(/filename\*=UTF-8''([^;]+)/i)
    if (utf8Match?.[1]) {
        return decodeURIComponent(utf8Match[1])
    }

    const plainMatch = headerValue.match(/filename="?([^"]+)"?/i)
    return plainMatch?.[1] || null
}

const downloadBlob = (blob: Blob, filename: string) => {
    const objectUrl = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = objectUrl
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(objectUrl)
}

const downloadUrl = (url: string, filename?: string) => {
    const link = document.createElement('a')
    link.href = url
    if (filename) {
        link.download = filename
    }
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
}

const parseAxiosErrorMessage = async (
    error: unknown,
): Promise<string | null> => {
    if (!axios.isAxiosError(error)) {
        return error instanceof Error ? error.message : null
    }

    if (
        error.response?.status === 405 &&
        error.config?.url?.includes('/history/export-dataset/tasks')
    ) {
        return HISTORY_EXPORT_TASK_API_UNAVAILABLE_MESSAGE
    }

    const responseData = error.response?.data
    if (responseData instanceof Blob) {
        try {
            const text = await responseData.text()
            if (text) {
                try {
                    const parsed = JSON.parse(text) as {
                        detail?: string
                        message?: string
                    }
                    return parsed.detail || parsed.message || text
                } catch {
                    return text
                }
            }
        } catch (blobError) {
            console.error('Failed to parse Axios error blob:', blobError)
        }
    }

    const detail = error.response?.data?.detail
    const messageText = error.response?.data?.message
    return (
        (typeof detail === 'string' && detail) ||
        (typeof messageText === 'string' && messageText) ||
        error.message
    )
}

const buildPaginatedCacheKey = (params: HistoryListQueryParams): string =>
    JSON.stringify(
        Object.entries(params)
            .filter(
                ([, value]) =>
                    value !== undefined && value !== null && value !== '',
            )
            .sort(([left], [right]) => left.localeCompare(right)),
    )

const normalizePestTypeParams = (
    values?: Array<string | null | undefined>
): string[] | undefined => {
    const normalizedValues = normalizeSearchValues(values)
    return normalizedValues.length > 0 ? normalizedValues : undefined
}

const normalizePaginatedParams = (
    params: HistoryListQueryParams = {},
): Required<Pick<HistoryListQueryParams, 'page' | 'page_size'>> &
    HistoryListQueryParams => ({
    page: params.page ?? 1,
    page_size: params.page_size ?? 10,
    type: params.type ?? 'image',
    sort_by: params.sort_by ?? 'timestamp',
    sort_order: params.sort_order ?? 'desc',
    start_timestamp: params.start_timestamp,
    end_timestamp: params.end_timestamp,
    region_id: params.region_id,
    region_ids:
        params.region_ids && params.region_ids.length > 0
            ? params.region_ids
            : undefined,
    filename: params.filename?.trim() || undefined,
    device_keyword: params.device_keyword?.trim() || undefined,
    device_name: params.device_name?.trim() || undefined,
    review_status: params.review_status,
    mode: params.mode,
    include_review_preview: params.include_review_preview,
    manual_recognition_status: params.manual_recognition_status,
    pest_types: normalizePestTypeParams(params.pest_types),
})

const ensureExactHistoryTotal = (
    data: PaginatedHistoryListResponse,
): PaginatedHistoryListResponse => {
    const totalStatus = String(data.total_status ?? 'ready')
    if (totalStatus !== 'ready') {
        throw new Error(`History list total is not exact: ${totalStatus}`)
    }
    return {
        ...data,
        total_status: 'ready',
    }
}

export const clearHistoryCache = (): void => {
    paginatedRequests.clear()
    detailRequests.clear()
    manualDetailRequests.clear()
}

export const getHistoryRecords = async (
    type?: 'image',
    forceRefresh = false,
): Promise<HistoryRecord[]> => {
    void forceRefresh
    try {
        const params = type ? { type } : undefined
        const response = await api.get<HistoryRecord[]>('/history/', {
            params,
        })

        let hydratedRecords = response.data
        try {
            hydratedRecords = await hydrateHistoryRecordsWithSignedUrls(
                response.data,
            )
        } catch (error) {
            console.error('Failed to hydrate history OSS URLs:', error)
        }

        return hydratedRecords
    } catch (error) {
        console.error('Failed to fetch history records:', error)
        return []
    }
}

export const getHistoryListPage = async (
    params: HistoryListQueryParams = {},
    forceRefresh = false,
): Promise<PaginatedHistoryListResponse> => {
    const normalizedParams = normalizePaginatedParams(params)
    const cacheKey = buildPaginatedCacheKey(normalizedParams)
    const requestKey = `history:${cacheKey}${forceRefresh ? ':force' : ''}`
    const existingRequest = paginatedRequests.get(requestKey)
    if (existingRequest) {
        return existingRequest
    }

    const request = (async () => {
        const requestParams = new URLSearchParams()
        Object.entries(normalizedParams).forEach(([key, value]) => {
            if (value === undefined || value === null || value === '') {
                return
            }

            if (Array.isArray(value)) {
                value.forEach((item) => {
                    requestParams.append(key, String(item))
                })
                return
            }

            requestParams.append(key, String(value))
        })
        const response = await api.get<PaginatedHistoryListResponse>(
            '/history/query',
            {
                params: requestParams,
            },
        )
        return ensureExactHistoryTotal(response.data)
    })()
    paginatedRequests.set(requestKey, request)
    try {
        return await request
    } finally {
        if (paginatedRequests.get(requestKey) === request) {
            paginatedRequests.delete(requestKey)
        }
    }
}

export const getHistoryRecord = async (
    id: string,
    options: { mode?: 'annotation' } = {},
): Promise<HistoryRecord | null> => {
    const requestKey = `${id}:${options.mode ?? 'display'}`
    const existingRequest = detailRequests.get(requestKey)
    if (existingRequest) {
        return existingRequest
    }

    const request = (async () => {
        try {
            const response = options.mode
                ? await api.get<HistoryRecord>(`/history/${id}`, {
                      params: { mode: options.mode },
                  })
                : await api.get<HistoryRecord>(`/history/${id}`)
            return response.data
        } catch (error) {
            console.error(`Failed to fetch history record ${id}:`, error)
            return null
        } finally {
            detailRequests.delete(requestKey)
        }
    })()

    detailRequests.set(requestKey, request)
    return request
}

export const getCachedHistoryRecord = (id: string): HistoryRecord | null => {
    void id
    return null
}

export const prefetchHistoryRecords = async (
    ids: string[],
    options: PrefetchHistoryRecordsOptions = {},
): Promise<void> => {
    const limit = options.limit ?? 5
    const concurrency = Math.max(1, options.concurrency ?? 2)
    const targetIds = Array.from(new Set(ids.filter(Boolean))).slice(0, limit)
    let nextIndex = 0

    const runWorker = async () => {
        while (nextIndex < targetIds.length) {
            const id = targetIds[nextIndex]
            nextIndex += 1
            try {
                await getHistoryRecord(id)
            } catch (error) {
                console.error(`Failed to prefetch history record ${id}:`, error)
            }
        }
    }

    await Promise.all(
        Array.from({ length: Math.min(concurrency, targetIds.length) }, () =>
            runWorker(),
        ),
    )
}

export const updateHistoryReview = async (
    id: string,
    payload: {
        status: HistoryReviewStatus
        note?: string | null
        predictions?: HistoryPrediction[]
        annotation_model_option_key?: string
        annotation_model_option_keys?: string[]
    },
    options?: MutationRequestOptions,
): Promise<HistoryRecord | null> => {
    try {
        const response = await api.patch<HistoryRecord>(
            `/history/${id}/review`,
            payload,
            getMutationRequestConfig(options),
        )
        clearHistoryCache()
        return response.data
    } catch (error) {
        console.error(`Failed to update history review ${id}:`, error)
        return null
    }
}

export const getAnnotationModelConfig =
    async (): Promise<HistoryAnnotationModelConfig | null> => {
        try {
            const response = await api.get<HistoryAnnotationModelConfig>(
                '/history/annotation-model-config',
            )
            return response.data
        } catch (error) {
            console.error('Failed to load annotation model config:', error)
            return null
        }
    }

export const updateAnnotationModelConfig = async (
    annotationModelOptionKeys: string[],
    observedModelVersionIds?: Record<string, number>,
    options?: MutationRequestOptions,
): Promise<HistoryAnnotationModelConfig | null> => {
    try {
        const response = await api.put<HistoryAnnotationModelConfig>(
            '/history/annotation-model-config',
            {
                annotation_model_option_keys: annotationModelOptionKeys,
                observed_model_version_ids: observedModelVersionIds,
            },
            getMutationRequestConfig(options),
        )
        clearHistoryCache()
        return response.data
    } catch (error) {
        console.error('Failed to update annotation model config:', error)
        return null
    }
}

export const getManualRecognitionListPage = async (
    params: HistoryListQueryParams = {},
    forceRefresh = false,
): Promise<PaginatedHistoryListResponse> => {
    const normalizedParams = normalizePaginatedParams(params)
    const cacheKey = buildPaginatedCacheKey({
        ...normalizedParams,
        mode: 'annotation',
    })
    const manualCacheKey = `manual-recognition:${cacheKey}${
        forceRefresh ? ':force' : ''
    }`
    const existingRequest = paginatedRequests.get(manualCacheKey)
    if (existingRequest) {
        return existingRequest
    }

    const request = (async () => {
        const requestParams = new URLSearchParams()
        ;(
            [
                'page',
                'page_size',
                'device_keyword',
                'device_name',
                'manual_recognition_status',
                'pest_types',
                'start_timestamp',
                'end_timestamp',
            ] as const
        ).forEach((key) => {
            const value = normalizedParams[key]
            if (value !== undefined && value !== null && value !== '') {
                if (Array.isArray(value)) {
                    value.forEach((item) => {
                        requestParams.append(key, String(item))
                    })
                    return
                }
                requestParams.append(key, String(value))
            }
        })
        const response = await api.get<PaginatedHistoryListResponse>(
            '/manual-recognition/query',
            {
                params: requestParams,
            },
        )
        return response.data
    })()
    paginatedRequests.set(manualCacheKey, request)
    try {
        return await request
    } finally {
        if (paginatedRequests.get(manualCacheKey) === request) {
            paginatedRequests.delete(manualCacheKey)
        }
    }
}

export const getManualRecognitionRecord = async (
    id: string,
): Promise<HistoryRecord | null> => {
    const existingRequest = manualDetailRequests.get(id)
    if (existingRequest) {
        return existingRequest
    }

    const request = (async () => {
        try {
            const response = await api.get<HistoryRecord>(
                `/manual-recognition/${id}`,
            )
            return response.data
        } catch (error) {
            console.error(
                `Failed to fetch manual recognition record ${id}:`,
                error,
            )
            return null
        } finally {
            manualDetailRequests.delete(id)
        }
    })()

    manualDetailRequests.set(id, request)
    return request
}

export const updateManualRecognition = async (
    id: string,
    payload: {
        status?: 'submitted'
        note?: string | null
        predictions: ManualRecognitionObjectInput[]
    },
    options?: MutationRequestOptions,
): Promise<HistoryRecord | null> => {
    try {
        const response = await api.put<HistoryRecord>(
            `/manual-recognition/${id}`,
            {
                status: payload.status ?? 'submitted',
                note: payload.note,
                predictions: payload.predictions,
            },
            getMutationRequestConfig(options),
        )
        clearHistoryCache()
        return response.data
    } catch (error) {
        console.error(`Failed to update manual recognition ${id}:`, error)
        return null
    }
}

export const exportHistoryDataset = async (
    payload: ExportHistoryDatasetPayload = {},
    options?: MutationRequestOptions,
): Promise<ExportHistoryDatasetResult> => {
    try {
        const response = await api.post('/history/export-dataset', payload, {
            ...getMutationRequestConfig(options),
            responseType: 'blob',
        })
        const filename =
            getFilenameFromDisposition(
                response.headers['content-disposition'],
            ) || 'history_review_dataset.zip'
        downloadBlob(response.data, filename)
        return { success: true }
    } catch (error) {
        console.error('Failed to export history dataset:', error)
        return {
            success: false,
            errorMessage: await parseAxiosErrorMessage(error),
        }
    }
}

export const createHistoryExportJob = async (
    payload: ExportHistoryDatasetPayload = {},
    options?: MutationRequestOptions,
): Promise<CreateHistoryExportJobResult> => {
    try {
        const response = await api.post<HistoryExportJob>(
            '/history/export-dataset/tasks',
            payload,
            getMutationRequestConfig(options),
        )
        return {
            success: true,
            job: response.data,
        }
    } catch (error) {
        console.error('Failed to create history export job:', error)
        return {
            success: false,
            errorMessage: await parseAxiosErrorMessage(error),
        }
    }
}

export const getHistoryExportJob = async (
    jobId: string,
): Promise<GetHistoryExportJobResult> => {
    try {
        const response = await api.get<HistoryExportJob>(
            `/history/export-dataset/tasks/${jobId}`,
        )
        return {
            success: true,
            job: response.data,
        }
    } catch (error) {
        console.error(`Failed to fetch history export job ${jobId}:`, error)
        return {
            success: false,
            errorMessage: await parseAxiosErrorMessage(error),
        }
    }
}

export const listHistoryExportJobs =
    async (): Promise<ListHistoryExportJobsResult> => {
        try {
            const response = await api.get<HistoryExportJob[]>(
                '/history/export-dataset/tasks',
            )
            return {
                success: true,
                jobs: response.data,
            }
        } catch (error) {
            console.error('Failed to fetch history export jobs:', error)
            return {
                success: false,
                jobs: [],
                errorMessage: await parseAxiosErrorMessage(error),
            }
        }
    }

export const getHistoryExportJobDownload = async (
    jobId: string,
): Promise<GetHistoryExportJobDownloadResult> => {
    try {
        const response = await api.get<HistoryExportJobDownload>(
            `/history/export-dataset/tasks/${jobId}/download`,
        )
        return {
            success: true,
            download: response.data,
        }
    } catch (error) {
        console.error(
            `Failed to fetch history export download ${jobId}:`,
            error,
        )
        return {
            success: false,
            errorMessage: await parseAxiosErrorMessage(error),
        }
    }
}

export const triggerHistoryExportDownload = (
    download: HistoryExportJobDownload,
): void => {
    downloadUrl(download.download_url, download.file_name)
}

export const deleteHistoryRecord = async (
    id: string,
    options?: MutationRequestOptions,
): Promise<boolean> => {
    try {
        await api.delete(`/history/${id}`, getMutationRequestConfig(options))
        clearHistoryCache()
        return true
    } catch (error) {
        console.error(`Failed to delete history record ${id}:`, error)
        return false
    }
}

export const clearAllHistory = async (
    options?: MutationRequestOptions,
): Promise<boolean> => {
    try {
        await api.delete('/history', getMutationRequestConfig(options))
        clearHistoryCache()
        return true
    } catch (error) {
        console.error('Failed to clear history records:', error)
        return false
    }
}

export const createHistoryRecord = async (
    data: Omit<HistoryRecord, 'id' | 'timestamp'>,
    options?: MutationRequestOptions,
): Promise<HistoryRecord | null> => {
    try {
        const response = await api.post<HistoryRecord>(
            '/history',
            data,
            getMutationRequestConfig(options),
        )
        clearHistoryCache()
        return response.data
    } catch (error) {
        console.error('Failed to create history record:', error)
        return null
    }
}

export const addHistoryRecord = async (
    record: HistoryRecord,
    options?: MutationRequestOptions,
): Promise<void> => {
    await api.post('/history/', record, getMutationRequestConfig(options))
    clearHistoryCache()
}

export const useHistory = (options: UseHistoryOptions = {}) => {
    const [records, setRecords] = useState<HistoryListItem[]>([])
    const [total, setTotal] = useState(0)
    const [totalStatus, setTotalStatus] = useState<HistoryTotalStatus>('ready')
    const [paginationMeta, setPaginationMeta] = useState({
        total_pages: 0,
        page_depth_limit: 0,
        max_accessible_page: 0,
        pagination_limited: false,
    })
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const requestIdRef = useRef(0)

    const {
        device_keyword,
        device_name,
        end_timestamp,
        include_review_preview: includeReviewPreview,
        mode,
        page,
        page_size,
        region_id,
        region_ids,
        review_status,
        pest_types,
        sort_by,
        sort_order,
        start_timestamp,
        type,
        forceRefreshKey,
    } = options
    const previousForceRefreshKeyRef = useRef(forceRefreshKey)
    const queryParams = useMemo(
        () =>
            normalizePaginatedParams({
                device_keyword,
                device_name,
                end_timestamp,
                mode,
                page,
                page_size,
                include_review_preview: includeReviewPreview,
                region_id,
                region_ids,
                pest_types,
                review_status,
                sort_by,
                sort_order,
                start_timestamp,
                type,
            }),
        [
            device_keyword,
            device_name,
            end_timestamp,
            includeReviewPreview,
            mode,
            page,
            page_size,
            region_id,
            region_ids,
            pest_types,
            review_status,
            sort_by,
            sort_order,
            start_timestamp,
            type,
        ],
    )

    const fetchRecords = useCallback(
        async (
            forceRefresh = false,
            options: RefreshHistoryRecordsOptions = {},
        ) => {
            requestIdRef.current += 1
            const requestId = requestIdRef.current
            if (!options.silent) {
                setLoading(true)
            }
            setError(null)
            try {
                const data = await getHistoryListPage(queryParams, forceRefresh)
                if (requestId !== requestIdRef.current) {
                    return
                }
                setRecords(data.items)
                setTotal(data.total)
                setTotalStatus(data.total_status ?? 'ready')
                setPaginationMeta({
                    total_pages: data.total_pages ?? 0,
                    page_depth_limit: data.page_depth_limit ?? 0,
                    max_accessible_page: data.max_accessible_page ?? 0,
                    pagination_limited: data.pagination_limited ?? false,
                })
            } catch (fetchError) {
                if (requestId !== requestIdRef.current) {
                    return
                }
                console.error('Failed to fetch history records:', fetchError)
                setRecords([])
                setTotal(0)
                setTotalStatus('ready')
                setPaginationMeta({
                    total_pages: 0,
                    page_depth_limit: 0,
                    max_accessible_page: 0,
                    pagination_limited: false,
                })
                setError('Failed to load history records')
            } finally {
                if (requestId === requestIdRef.current) {
                    if (!options.silent) {
                        setLoading(false)
                    }
                }
            }
        },
        [queryParams],
    )

    useEffect(() => {
        const shouldForceRefresh =
            forceRefreshKey !== undefined &&
            previousForceRefreshKeyRef.current !== undefined &&
            previousForceRefreshKeyRef.current !== forceRefreshKey

        previousForceRefreshKeyRef.current = forceRefreshKey
        fetchRecords(shouldForceRefresh)
    }, [fetchRecords, forceRefreshKey])

    const deleteRecord = useCallback(
        async (id: string) => {
            try {
                const success = await deleteHistoryRecord(id)
                if (success) {
                    await fetchRecords(true)
                }
                return success
            } catch (deleteError) {
                console.error(
                    `Failed to delete history record ${id}:`,
                    deleteError,
                )
                setError('Failed to delete history record')
                return false
            }
        },
        [fetchRecords],
    )

    const clearAllRecords = useCallback(async () => {
        try {
            const success = await clearAllHistory()
            if (success) {
                await fetchRecords(true)
            }
            return success
        } catch (clearError) {
            console.error('Failed to clear history records:', clearError)
            setError('Failed to clear history records')
            return false
        }
    }, [fetchRecords])

    const updateReview = useCallback(
        async (
            id: string,
            payload: {
                status: HistoryReviewStatus
                note?: string | null
                predictions?: HistoryPrediction[]
                annotation_model_option_key?: string
                annotation_model_option_keys?: string[]
            },
        ) => {
            try {
                const updatedRecord = await updateHistoryReview(id, payload)
                if (updatedRecord) {
                    await fetchRecords(true)
                }
                return updatedRecord
            } catch (updateError) {
                console.error(
                    `Failed to update history review ${id}:`,
                    updateError,
                )
                setError('Failed to update history review')
                return null
            }
        },
        [fetchRecords],
    )

    return {
        records,
        total,
        totalStatus,
        paginationMeta,
        loading,
        error,
        refreshRecords: fetchRecords,
        deleteRecord,
        clearAllRecords,
        updateReview,
    }
}
